/**
 * Mobile navigation overlay toggle (hamburger).
 *
 * Control spec:
 *  - The burger button (in the utility bar) toggles the full-screen overlay.
 *  - aria-expanded on the button + [hidden] on the overlay drive state; the
 *    burger animates to an X via [aria-expanded="true"] in CSS.
 *  - Open locks document scroll and moves focus to the first menu link; close
 *    restores scroll and returns focus to the button.
 *  - Escape closes; activating any link closes (same-page anchors need the
 *    overlay gone to reveal the target).
 *  - Only relevant below the 1229px breakpoint, but the JS is harmless above it
 *    (the button + overlay are display:none, so nothing is interactable).
 */
export function initMobileMenu() {
  const button = document.querySelector('[data-emc-menu-toggle]');
  const menu = document.querySelector('[data-emc-menu]');
  if (!button || !menu) return;

  let isOpen = false;

  const firstLink = () => menu.querySelector('a, button');

  const setState = (open) => {
    isOpen = open;
    button.setAttribute('aria-expanded', String(open));
    button.setAttribute(
      'aria-label',
      open
        ? button.dataset.labelClose || 'Close menu'
        : button.dataset.labelOpen || 'Open menu'
    );
    menu.hidden = !open;
    document.documentElement.classList.toggle('emc-menu-open', open);
  };

  const open = () => {
    setState(true);
    firstLink()?.focus();
  };

  const close = ({ restoreFocus = true } = {}) => {
    setState(false);
    if (restoreFocus) button.focus();
  };

  button.addEventListener('click', () => (isOpen ? close() : open()));

  // Escape closes from anywhere in the menu or the button.
  document.addEventListener('keydown', (e) => {
    if (isOpen && e.key === 'Escape') close();
  });

  // Activating a link closes the menu (don't steal focus back to the button).
  // Tapping the transparent backdrop outside the panel closes it too.
  menu.addEventListener('click', (e) => {
    if (e.target.closest('a')) {
      close({ restoreFocus: false });
    } else if (!e.target.closest('.emc-mobile-menu__panel')) {
      close();
    }
  });

  // If the viewport grows past the breakpoint while open, close so the desktop
  // chrome isn't left in the locked-scroll state.
  const desktop = window.matchMedia('(min-width: 1229px)');
  desktop.addEventListener('change', (e) => {
    if (e.matches && isOpen) close({ restoreFocus: false });
  });

  setState(false);
}
